/**
 * APX WorkCenter -- DashboardLayout
 * Design: "Gold Standard" -- Premium Editorial Finance
 * Persistent left sidebar with role-based navigation + top header bar.
 * Stage 2: NotificationBell added to header.
 * Stage 3: Security banner prompting password change on first login.
 */

import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { useAuth, UserRole } from "@/contexts/AuthContext";
import { usePreview, useEffectiveRole, PreviewableRole } from "@/contexts/PreviewContext";
import { useAdminNotificationStream } from "@/hooks/useAdminNotificationStream";
import { toast } from "sonner";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import NotificationBell from "@/components/NotificationBell";
import XeroStatusDots from "@/components/XeroStatusDots";
import { trpc } from "@/lib/trpc";

const HEADER_BG =
  "https://d2xsxph8kpxj0f.cloudfront.net/310519663412810690/7y9xgY6crGLAQqoXCAMXSi/apx-dashboard-header-oMVNpvqy9oqaEfXcPjvPrn.webp";

// Key used to track whether the user has dismissed the security banner this session
const BANNER_DISMISSED_KEY = "apx_pw_banner_dismissed";

// ─── Nav item types ──────────────────────────────────────────────────────────
interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  roles: UserRole[];
  isPlaceholder?: boolean;
  isMarian?: boolean;
  externalUrl?: string;
}

// ─── Icons (inline SVG, no extra deps) ──────────────────────────────────────
const Icon = {
  grid: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  ),
  users: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 00-5-3.87M9 20H4v-2a4 4 0 015-3.87m6-4a4 4 0 11-8 0 4 4 0 018 0zm6 4a3 3 0 10-6 0" />
    </svg>
  ),
  briefcase: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M20 7H4a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2zM16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" />
    </svg>
  ),
  chart: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
  calendar: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  bell: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  ),
  book: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  ),
  checkSquare: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  ),
  settings: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  xero: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm-1.25 16.5l-3.5-3.5 1.06-1.06 2.44 2.44 4.94-4.94 1.06 1.06-6 6z"/>
    </svg>
  ),
  hub: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
    </svg>
  ),
  folder: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
    </svg>
  ),
  drive: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 15l4.5-8h9L21 15M3 15h18M3 15l2 4h14l2-4" />
    </svg>
  ),
  logout: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
    </svg>
  ),
  shield: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  ),
  key: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
    </svg>
  ),
  x: (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  payments: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  ),
  intelligence: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
    </svg>
  ),
  redTeam: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  ),
  mail: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
    </svg>
  ),
  sms: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
    </svg>
  ),
  contacts: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  ),
  penLine: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
    </svg>
  ),
  research: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3a9 9 0 100 18 9 9 0 000-18zM12 8v4l2.5 1.5M7.5 4.8l-1.1-1.1M16.5 4.8l1.1-1.1" />
    </svg>
  ),
  report: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  calculator: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <rect x="4" y="2.75" width="16" height="18.5" rx="2.25" />
      <path d="M7.5 6.75h9M7.75 11h.01M12 11h.01M16.25 11h.01M7.75 15h.01M12 15h.01M16.25 15h.01M7.75 18.25h.01M12 18.25h4.25" strokeLinecap="round" />
    </svg>
  ),
};

// ─── Navigation items ────────────────────────────────────────────────────────
const NAV_ITEMS: NavItem[] = [
  // Super Admin (Mr. B exclusive)
  { label: "Firm Overview", path: "/dashboard", icon: Icon.grid, roles: ["super_admin", "admin"] },
  { label: "Biz Model Stress Test", path: "/red-team", icon: Icon.redTeam, roles: ["super_admin", "admin"] },
  { label: "Email Inbox", path: "/email-inbox", icon: Icon.mail, roles: ["super_admin", "admin"] },
  { label: "SMS Sent / Received", path: "/sms-inbox", icon: Icon.sms, roles: ["super_admin", "admin"] },
  { label: "Contacts", path: "/clients", icon: Icon.contacts, roles: ["super_admin", "admin"] },
  { label: "Drive Docs", path: "/drive", icon: Icon.drive, roles: ["super_admin", "admin"] },
  { label: "Client Analytics", path: "/client-analytics-overview", icon: Icon.chart, roles: ["super_admin", "admin"] },
  { label: "Xero|Manus Active", path: "/client-analytics", icon: Icon.chart, roles: ["super_admin", "admin"] },
  { label: "Xero Reports", path: "/xero-reports", icon: Icon.xero, roles: ["super_admin", "admin"] },
  { label: "Platform Costs", path: "/costs", icon: Icon.chart, roles: ["super_admin", "admin"] },
  { label: "Payments", path: "/payments", icon: Icon.payments, roles: ["super_admin", "admin"] },
  { label: "My Tasks", path: "/my-tasks", icon: Icon.checkSquare, roles: ["super_admin", "admin"] },
  { label: "Task Requests", path: "/task-requests", icon: Icon.bell, roles: ["super_admin", "admin"] },
  { label: "Xero Client Mgmt", path: "/xero-client-management", icon: Icon.xero, roles: ["super_admin", "admin"] },
  { label: "Portfolio Work Report", path: "/portfolio-report", icon: Icon.report, roles: ["super_admin", "admin"] },
  { label: "Pricing Calculator", path: "/pricing-calculator", icon: Icon.calculator, roles: ["super_admin", "admin"] },
  { label: "Depreciation Calc/ WkSht", path: "/depreciation-worksheet", icon: Icon.calculator, roles: ["super_admin", "admin"] },
  { label: "AI Writing Studio", path: "/ai-writing", icon: Icon.penLine, roles: ["super_admin", "admin"] },
  { label: "Research Desk", path: "/ai-research", icon: Icon.research, roles: ["super_admin", "admin"] },
  { label: "Settings", path: "/settings", icon: Icon.settings, roles: ["super_admin", "admin"] },

  // Controller
  { label: "My Dashboard", path: "/dashboard", icon: Icon.grid, roles: ["controller"] },
  { label: "Biz Model Stress Test", path: "/red-team", icon: Icon.redTeam, roles: ["controller"] },
  { label: "Email Inbox", path: "/email-inbox", icon: Icon.mail, roles: ["controller"] },
  { label: "SMS Sent / Received", path: "/sms-inbox", icon: Icon.sms, roles: ["controller"] },
  { label: "Contacts", path: "/clients", icon: Icon.contacts, roles: ["controller"] },
  { label: "Drive Docs", path: "/drive", icon: Icon.drive, roles: ["controller"] },
  { label: "Client Analytics", path: "/client-analytics-overview", icon: Icon.chart, roles: ["controller"] },
  { label: "Xero|Manus Active", path: "/client-analytics", icon: Icon.chart, roles: ["controller"] },
  { label: "Xero Reports", path: "/xero-reports", icon: Icon.xero, roles: ["controller"] },
  { label: "Payments", path: "/payments", icon: Icon.payments, roles: ["controller"] },

  { label: "My Tasks", path: "/my-tasks", icon: Icon.checkSquare, roles: ["controller"] },
  { label: "Task Requests", path: "/task-requests", icon: Icon.bell, roles: ["controller"] },
  { label: "Xero Client Mgmt", path: "/xero-client-management", icon: Icon.xero, roles: ["controller"] },
  { label: "Portfolio Work Report", path: "/portfolio-report", icon: Icon.report, roles: ["controller"] },
  { label: "Pricing Calculator", path: "/pricing-calculator", icon: Icon.calculator, roles: ["controller"] },
  { label: "Depreciation Calc/ WkSht", path: "/depreciation-worksheet", icon: Icon.calculator, roles: ["controller"] },
  { label: "AI Writing Studio", path: "/ai-writing", icon: Icon.penLine, roles: ["controller"] },
  { label: "Research Desk", path: "/ai-research", icon: Icon.research, roles: ["controller"] },
  { label: "Team Access & Add Client", path: "/settings", icon: Icon.users, roles: ["controller"] },
  // Controller has no Settings nav — Change Password is in the sidebar footer

  // Asst Controller — same as Controller minus Team & Access and Payments
  { label: "My Dashboard", path: "/dashboard", icon: Icon.grid, roles: ["asst_controller"] },
  { label: "Biz Model Stress Test", path: "/red-team", icon: Icon.redTeam, roles: ["asst_controller"] },
  { label: "Email Inbox", path: "/email-inbox", icon: Icon.mail, roles: ["asst_controller"] },
  { label: "SMS Sent / Received", path: "/sms-inbox", icon: Icon.sms, roles: ["asst_controller"] },
  { label: "Contacts", path: "/clients", icon: Icon.contacts, roles: ["asst_controller"] },
  { label: "Drive Docs", path: "/drive", icon: Icon.drive, roles: ["asst_controller"] },
  { label: "Client Analytics", path: "/client-analytics-overview", icon: Icon.chart, roles: ["asst_controller"] },
  { label: "Xero|Manus Active", path: "/client-analytics", icon: Icon.chart, roles: ["asst_controller"] },
  { label: "Xero Reports", path: "/xero-reports", icon: Icon.xero, roles: ["asst_controller"] },
  { label: "My Tasks", path: "/my-tasks", icon: Icon.checkSquare, roles: ["asst_controller"] },
  { label: "Task Requests", path: "/task-requests", icon: Icon.bell, roles: ["asst_controller"] },
  { label: "Xero Client Mgmt", path: "/xero-client-management", icon: Icon.xero, roles: ["asst_controller"] },
  { label: "Portfolio Work Report", path: "/portfolio-report", icon: Icon.report, roles: ["asst_controller"] },
  { label: "Pricing Calculator", path: "/pricing-calculator", icon: Icon.calculator, roles: ["asst_controller"] },
  { label: "Depreciation Calc/ WkSht", path: "/depreciation-worksheet", icon: Icon.calculator, roles: ["asst_controller"] },
  { label: "AI Writing Studio", path: "/ai-writing", icon: Icon.penLine, roles: ["asst_controller"] },
  { label: "Research Desk", path: "/ai-research", icon: Icon.research, roles: ["asst_controller"] },
  // No Team & Access, no Payments for Asst Controller

  // Bookkeeping Manager + Bookkeeping Supervisor (Irit) — identical UI
  { label: "My Dashboard", path: "/dashboard", icon: Icon.grid, roles: ["bookkeeping_manager", "bookkeeper_supervisor"] },
  { label: "APX Intelligence", path: "/intelligence", icon: Icon.intelligence, roles: ["bookkeeping_manager", "bookkeeper_supervisor"] },
  { label: "Email Inbox", path: "/email-inbox", icon: Icon.mail, roles: ["bookkeeping_manager", "bookkeeper_supervisor"] },
  { label: "SMS Sent / Received", path: "/sms-inbox", icon: Icon.sms, roles: ["bookkeeping_manager", "bookkeeper_supervisor"] },
  { label: "Contacts", path: "/clients", icon: Icon.contacts, roles: ["bookkeeping_manager", "bookkeeper_supervisor"] },
  { label: "Drive Docs", path: "/drive", icon: Icon.drive, roles: ["bookkeeping_manager", "bookkeeper_supervisor"] },
  { label: "Client Analytics", path: "/client-analytics-overview", icon: Icon.chart, roles: ["bookkeeping_manager", "bookkeeper_supervisor"] },
  { label: "Xero|Manus Active", path: "/client-analytics", icon: Icon.chart, roles: ["bookkeeping_manager", "bookkeeper_supervisor"] },
  { label: "Xero Reports", path: "/xero-reports", icon: Icon.xero, roles: ["bookkeeping_manager", "bookkeeper_supervisor"] },
  { label: "My Tasks", path: "/my-tasks", icon: Icon.checkSquare, roles: ["bookkeeping_manager", "bookkeeper_supervisor"] },
  { label: "Task Requests", path: "/task-requests", icon: Icon.bell, roles: ["bookkeeping_manager", "bookkeeper_supervisor"] },
  { label: "AI Writing Studio", path: "/ai-writing", icon: Icon.penLine, roles: ["bookkeeping_manager", "bookkeeper_supervisor"] },
  { label: "Research Desk", path: "/ai-research", icon: Icon.research, roles: ["bookkeeping_manager", "bookkeeper_supervisor"] },
  { label: "Depreciation Calc/ WkSht", path: "/depreciation-worksheet", icon: Icon.calculator, roles: ["bookkeeping_manager", "bookkeeper_supervisor"] },

  // Tax Division
  { label: "My Dashboard", path: "/dashboard", icon: Icon.grid, roles: ["tax_division"] },
  { label: "APX Intelligence", path: "/intelligence", icon: Icon.intelligence, roles: ["tax_division"] },
  { label: "Email Inbox", path: "/email-inbox", icon: Icon.mail, roles: ["tax_division"] },
  { label: "SMS Sent / Received", path: "/sms-inbox", icon: Icon.sms, roles: ["tax_division"] },
  { label: "Contacts", path: "/clients", icon: Icon.contacts, roles: ["tax_division"] },
  { label: "Drive Docs", path: "/drive", icon: Icon.drive, roles: ["tax_division"] },
  { label: "Client Analytics", path: "/client-analytics-overview", icon: Icon.chart, roles: ["tax_division"] },
  { label: "Xero|Manus Active", path: "/client-analytics", icon: Icon.chart, roles: ["tax_division"] },
  { label: "Xero Reports", path: "/xero-reports", icon: Icon.xero, roles: ["tax_division"] },
  { label: "My Tasks", path: "/my-tasks", icon: Icon.checkSquare, roles: ["tax_division"] },
  { label: "Task Requests", path: "/task-requests", icon: Icon.bell, roles: ["tax_division"] },
  { label: "AI Writing Studio", path: "/ai-writing", icon: Icon.penLine, roles: ["tax_division"] },
  { label: "Research Desk", path: "/ai-research", icon: Icon.research, roles: ["tax_division"] },
  { label: "Depreciation Calc/ WkSht", path: "/depreciation-worksheet", icon: Icon.calculator, roles: ["tax_division"] },

  // Bookkeeper — no Intelligence access
  { label: "My Clients", path: "/dashboard", icon: Icon.grid, roles: ["bookkeeper"] },
  { label: "Email Inbox", path: "/email-inbox", icon: Icon.mail, roles: ["bookkeeper"] },
  { label: "SMS Sent / Received", path: "/sms-inbox", icon: Icon.sms, roles: ["bookkeeper"] },
  { label: "Contacts", path: "/clients", icon: Icon.contacts, roles: ["bookkeeper"] },
  { label: "Drive Docs", path: "/drive", icon: Icon.drive, roles: ["bookkeeper"] },
  { label: "Payments", path: "/payments", icon: Icon.payments, roles: ["bookkeeper"], isMarian: true },
  { label: "Client Analytics", path: "/client-analytics-overview", icon: Icon.chart, roles: ["bookkeeper"] },
  { label: "Xero|Manus Active", path: "/client-analytics", icon: Icon.chart, roles: ["bookkeeper"] },
  { label: "Xero Reports", path: "/xero-reports", icon: Icon.xero, roles: ["bookkeeper"] },
  { label: "My Tasks", path: "/my-tasks", icon: Icon.checkSquare, roles: ["bookkeeper"] },
  { label: "Task Requests", path: "/task-requests", icon: Icon.bell, roles: ["bookkeeper"] },
  { label: "AI Writing Studio", path: "/ai-writing", icon: Icon.penLine, roles: ["bookkeeper"] },
  { label: "Research Desk", path: "/ai-research", icon: Icon.research, roles: ["bookkeeper"] },
  { label: "Depreciation Calc/ WkSht", path: "/depreciation-worksheet", icon: Icon.calculator, roles: ["bookkeeper"] },

  // Shared team workspace
  { label: "Team Notes", path: "/team-notes", icon: Icon.penLine, roles: ["super_admin", "admin", "controller", "asst_controller", "bookkeeping_manager", "bookkeeper_supervisor", "bookkeeper", "tax_division"] },

];

// ─── External tool quick links (shown in header for all roles) ───────────────
const EXTERNAL_TOOLS = [
  {
    label: "Xero",
    url: "https://go.xero.com",
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm-1.25 16.5l-3.5-3.5 1.06-1.06 2.44 2.44 4.94-4.94 1.06 1.06-6 6z"/>
      </svg>
    ),
    color: "oklch(0.08 0.01 240)",
  },
  {
    label: "Gmail",
    url: "https://mail.google.com",
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 010 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
      </svg>
    ),
    color: "oklch(0.08 0.01 240)",
  },
  {
    label: "Calendar",
    url: "https://calendar.google.com",
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    color: "oklch(0.08 0.01 240)",
  },
];

const ROLE_LABELS: Record<UserRole, string> = {
  super_admin: "DIRECTOR",
  admin: "DIRECTOR",
  controller: "CONTROLLER",
  asst_controller: "ASST CNTLR",
  bookkeeping_manager: "BKKPR MGR",
  bookkeeper_supervisor: "BKKPR SUPVR",
  bookkeeper: "BKKPR",
  tax_division: "TAX DIV.",
};

const PREVIEW_ROLE_OPTIONS: { value: PreviewableRole; label: string }[] = [
  { value: "controller", label: "Controller" },
  { value: "asst_controller", label: "Asst Cntlr" },
  { value: "bookkeeping_manager", label: "Bkkpr Mgr" },
  { value: "bookkeeper", label: "Bookkeeper" },
  { value: "tax_division", label: "Tax Division" },
];

const ROLE_COLORS: Record<UserRole, string> = {
  super_admin: "oklch(0.35 0.06 240)",
  admin: "oklch(0.35 0.06 240)",
  controller: "oklch(0.30 0.06 240)",
  asst_controller: "oklch(0.30 0.06 240)",
  bookkeeping_manager: "oklch(0.30 0.06 240)",
  bookkeeper_supervisor: "oklch(0.30 0.06 240)",
  bookkeeper: "oklch(0.30 0.06 240)",
  tax_division: "oklch(0.30 0.06 240)",
};

// ─── Component ───────────────────────────────────────────────────────────────
export default function DashboardLayout({ children, previewEmail, noPadding }: { children: React.ReactNode; previewEmail?: string; noPadding?: boolean }) {
  const { user, logout } = useAuth();
  const { previewRole, setPreviewRole, isPreviewMode, exitPreview } = usePreview();
  const [location] = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    // Persist sidebar state in localStorage — default open for all roles
    const stored = localStorage.getItem("apx_sidebar_open");
    return stored === null ? true : stored === "true";
  });
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showSecurityBanner, setShowSecurityBanner] = useState(false);

  // Real-time admin notification unread count via SSE (super_admin only)
  const { unreadCount: adminUnreadCount } = useAdminNotificationStream(!!user);
  // Poll for unread email count every 60s to show badge on sidebar nav
  const { data: emailUnread } = trpc.email.unreadCount.useQuery(undefined, {
    enabled: !!user,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  // Poll for unread SMS count every 60s to show badge on SMS Inbox nav
  const { data: smsUnread } = trpc.zapier.summary.useQuery(undefined, {
    enabled: !!user,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const smsUnreadCount = smsUnread?.unreviewedSms ?? 0;

  // Poll for task notification count every 30s to show badge on Task Requests nav
  const { data: taskNotifCount } = trpc.taskNotifications.count.useQuery(
    { recipientEmail: user?.email ?? "" },
    { enabled: !!user?.email, refetchInterval: 30_000, staleTime: 15_000 }
  );

  // Poll for Drive Docs recent uploads count every 5 min to show badge on Drive Docs nav
  const { data: driveRecentData } = trpc.drive.recentUploadsCount.useQuery(
    { days: 7 },
    { enabled: !!user, refetchInterval: 5 * 60_000, staleTime: 4 * 60_000 }
  );
  const driveRecentCount = driveRecentData?.count ?? 0;

  // Show security banner once per session — permanently hidden once user has changed their password
  useEffect(() => {
    if (!user) return;
    // If the user has already changed their password (DB flag), never show the banner
    if (user.hasChangedPassword) {
      setShowSecurityBanner(false);
      return;
    }
    const dismissed = sessionStorage.getItem(`${BANNER_DISMISSED_KEY}_${user.id}`);
    if (!dismissed) {
      setShowSecurityBanner(true);
    }
  }, [user]);

  const dismissBanner = () => {
    if (user) {
      sessionStorage.setItem(`${BANNER_DISMISSED_KEY}_${user.id}`, "1");
    }
    setShowSecurityBanner(false);
  };

  const handleChangePasswordFromBanner = () => {
    dismissBanner();
    setShowChangePassword(true);
  };

  // Effective role for nav/display — uses preview role if set
  // MUST be called BEFORE any early return (React rules of hooks)
  const effectiveRole = useEffectiveRole(user?.role ?? "bookkeeper" as UserRole);

  if (!user) return null;
  const MARIAN_EMAIL = "marianjoyhernandez2@gmail.com";
  const isMarian =
    user.email?.toLowerCase() === MARIAN_EMAIL ||
    (previewEmail?.toLowerCase() === MARIAN_EMAIL);
  const navItems = NAV_ITEMS.filter((item) => {
    if (!item.roles.includes(effectiveRole)) return false;
    // Payments link is only for Marian among bookkeepers
    if ((item as any).isMarian && effectiveRole === "bookkeeper" && !isMarian) return false;
    return true;
  });
  const roleColor = ROLE_COLORS[effectiveRole];

  const handleNavClick = (item: NavItem, e: React.MouseEvent) => {
    if (item.externalUrl) {
      e.preventDefault();
      window.open(item.externalUrl, "_blank", "noopener,noreferrer");
      return;
    }
    if (item.isPlaceholder) {
      e.preventDefault();
      toast.info("Feature coming soon", {
        description: `${item.label} will be available in the next build stage.`,
      });
    }
  };

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: "#F8F6F2" }}>

      {/* ── Sidebar ── */}
      <aside
        className="flex flex-col shrink-0 transition-all duration-300 overflow-hidden"
        style={{
          width: sidebarOpen ? "240px" : "64px",
          background: "#EDE8E0",
          borderRight: "1px solid #D8D2C8",
        }}
      >
        {/* Sidebar header */}
        <div
          className="flex items-center border-b relative"
          style={{
            borderColor: "oklch(0.88 0.01 240)",
            padding: sidebarOpen ? "8px 12px" : "8px 0",
            minHeight: "52px",
            justifyContent: sidebarOpen ? "flex-start" : "center",
          }}
        >
          {/* Logo */}
          <img
            src="https://d2xsxph8kpxj0f.cloudfront.net/310419663029793542/5GqF8Kn9QcwXve8h8refaY/apx-logo-final_808b0f18.png"
            alt="APX"
            className="object-contain shrink-0"
            style={{ height: "48px", width: "auto" }}
          />
          {/* Name — only when expanded */}
          {sidebarOpen && (
            <div className="ml-3 flex flex-col items-center leading-tight">
              <span
                className="text-base font-black tracking-widest uppercase"
                style={{ fontFamily: "'DM Sans', sans-serif", color: "oklch(0.12 0.04 240)", letterSpacing: "0.18em", lineHeight: 1.15 }}
              >
                APX
              </span>
              <span
                className="text-base font-black tracking-widest uppercase"
                style={{ fontFamily: "'DM Sans', sans-serif", color: "oklch(0.12 0.04 240)", letterSpacing: "0.18em", lineHeight: 1.15 }}
              >
                WORKCENTER
              </span>
            </div>
          )}
          {/* Toggle button — absolute top-right corner */}
          {sidebarOpen && (
            <button
              onClick={() => { const next = !sidebarOpen; setSidebarOpen(next); localStorage.setItem("apx_sidebar_open", String(next)); }}
              className="absolute top-3 right-2 w-7 h-7 flex items-center justify-center rounded-full transition-all"
              style={{ color: "#ffffff", background: "rgba(60,55,50,0.88)", border: "2px solid rgba(255,255,255,0.55)", boxShadow: "0 2px 6px rgba(0,0,0,0.40)", filter: "drop-shadow(0 0 3px rgba(255,255,255,0.35))" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "rgba(40,35,30,0.95)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "rgba(60,55,50,0.88)"; }}
              title="Collapse sidebar"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </button>
          )}
          {/* Expand button when collapsed — overlaid on the logo */}
          {!sidebarOpen && (
            <button
              onClick={() => { const next = !sidebarOpen; setSidebarOpen(next); localStorage.setItem("apx_sidebar_open", String(next)); }}
              className="absolute inset-0 flex items-center justify-center"
              style={{ background: "transparent" }}
              title="Expand sidebar"
            >
              <span
                className="flex items-center justify-center w-7 h-7 rounded-full"
                style={{ background: "rgba(60,55,50,0.88)", border: "2px solid rgba(255,255,255,0.55)", boxShadow: "0 2px 6px rgba(0,0,0,0.40)", filter: "drop-shadow(0 0 3px rgba(255,255,255,0.35))" }}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={2.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              </span>
            </button>
          )}
        </div>

        {/* Gold separator */}
        <div style={{ height: "1px", margin: "0 16px", background: "oklch(0.72 0.14 75)" }} />

        {/* Nav items */}
        <nav className="flex-1 overflow-y-auto py-1 px-2 space-y-0.5">
          {navItems.map((item) => {
            const isActive = location === item.path;
            return (
              <Link
                key={item.label}
                href={item.isPlaceholder ? "#" : item.path}
                onClick={(e: React.MouseEvent) => handleNavClick(item, e)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150 group relative"
                style={{
                  background: isActive ? `${roleColor}15` : "transparent",
                  color: isActive ? roleColor : "oklch(0.28 0.04 240)",
                  textDecoration: "none",
                  display: "flex",
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    (e.currentTarget as HTMLElement).style.background = "oklch(0.92 0.01 240)";
                    (e.currentTarget as HTMLElement).style.color = "oklch(0.12 0.04 240)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    (e.currentTarget as HTMLElement).style.background = "transparent";
                    (e.currentTarget as HTMLElement).style.color = "oklch(0.28 0.04 240)";
                  }
                }}
              >
                {isActive && (
                  <span
                    className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 rounded-r"
                    style={{ background: roleColor }}
                  />
                )}
                {/* Email / Task-Request badge on icon when sidebar is collapsed */}
                <span className="shrink-0 relative">
                  {item.icon}
                  {!sidebarOpen && item.path === "/email-inbox" && (emailUnread?.count ?? 0) > 0 && (
                    <span
                      className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full flex items-center justify-center text-[8px] font-bold"
                      style={{ background: "oklch(0.55 0.01 240)", color: "white" }}
                    >
                      {(emailUnread?.count ?? 0) > 99 ? "99+" : emailUnread?.count}
                    </span>
                  )}
                  {!sidebarOpen && item.path === "/task-requests" && (taskNotifCount?.count ?? 0) > 0 && (
                    <span
                      className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full flex items-center justify-center text-[8px] font-bold"
                      style={{ background: "oklch(0.82 0.01 240)", color: "oklch(0.12 0.04 240)" }}
                    >
                      {(taskNotifCount?.count ?? 0) > 99 ? "99+" : taskNotifCount?.count}
                    </span>
                  )}
                  {!sidebarOpen && item.path === "/sms-inbox" && smsUnreadCount > 0 && (
                    <span
                      className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full flex items-center justify-center text-[8px] font-bold"
                      style={{ background: "oklch(0.55 0.01 240)", color: "white" }}
                    >
                      {smsUnreadCount > 99 ? "99+" : smsUnreadCount}
                    </span>
                  )}
                  {/* Admin notification unread badge on Firm Overview icon (collapsed, super_admin only) */}
                  {!sidebarOpen && item.path === "/dashboard" && user.role === "super_admin" || user.role === "admin" && adminUnreadCount > 0 && (
                    <span
                      className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full flex items-center justify-center text-[8px] font-bold"
                      style={{ background: "oklch(0.82 0.01 240)", color: "oklch(0.12 0.04 240)" }}
                    >
                      {adminUnreadCount > 99 ? "99+" : adminUnreadCount}
                    </span>
                  )}
                  {/* Drive Docs recent uploads badge (collapsed) */}
                  {!sidebarOpen && item.path === "/drive" && driveRecentCount > 0 && (
                    <span
                      className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full flex items-center justify-center text-[8px] font-bold"
                      style={{ background: "oklch(0.55 0.14 75)", color: "white" }}
                    >
                      {driveRecentCount > 99 ? "99+" : driveRecentCount}
                    </span>
                  )}
                </span>
                {sidebarOpen && (
                  <span className="text-sm font-medium truncate">{item.label}</span>
                )}
                {/* Unread email count badge (sidebar open) */}
                {sidebarOpen && item.path === "/email-inbox" && (emailUnread?.count ?? 0) > 0 && (
                  <span
                    className="ml-auto text-[9px] px-1.5 py-0.5 rounded-full font-bold"
                    style={{ background: "oklch(0.92 0.005 240 / 20%)", color: "oklch(0.08 0.01 240)" }}
                  >
                    {(emailUnread?.count ?? 0) > 99 ? "99+" : emailUnread?.count}
                  </span>
                )}
                {/* Task request count badge (sidebar open) */}
                {sidebarOpen && item.path === "/task-requests" && (taskNotifCount?.count ?? 0) > 0 && (
                  <span
                    className="ml-auto text-[9px] px-1.5 py-0.5 rounded-full font-bold"
                    style={{ background: "oklch(0.92 0.005 240 / 20%)", color: "oklch(0.08 0.01 240)" }}
                  >
                    {(taskNotifCount?.count ?? 0) > 99 ? "99+" : taskNotifCount?.count}
                  </span>
                )}
                {/* SMS unread badge (sidebar open) */}
                {sidebarOpen && item.path === "/sms-inbox" && smsUnreadCount > 0 && (
                  <span
                    className="ml-auto text-[9px] px-1.5 py-0.5 rounded-full font-bold"
                    style={{ background: "oklch(0.92 0.005 240 / 20%)", color: "oklch(0.08 0.01 240)" }}
                  >
                    {smsUnreadCount > 99 ? "99+" : smsUnreadCount}
                  </span>
                )}
                {/* Admin notification unread badge on Firm Overview (sidebar open, super_admin only) */}
                {sidebarOpen && item.path === "/dashboard" && user.role === "super_admin" || user.role === "admin" && adminUnreadCount > 0 && (
                  <span
                    className="ml-auto text-[9px] px-1.5 py-0.5 rounded-full font-bold"
                    style={{ background: "oklch(0.92 0.005 240 / 20%)", color: "oklch(0.08 0.01 240)" }}
                  >
                    {adminUnreadCount > 99 ? "99+" : adminUnreadCount}
                  </span>
                )}
                {/* Drive Docs recent uploads badge (sidebar open) — shows count of files uploaded in last 7 days */}
                {sidebarOpen && item.path === "/drive" && driveRecentCount > 0 && (
                  <span
                    className="ml-auto text-[9px] px-1.5 py-0.5 rounded-full font-bold"
                    style={{ background: "oklch(0.55 0.14 75 / 20%)", color: "oklch(0.35 0.10 75)" }}
                  >
                    {driveRecentCount > 99 ? "99+" : driveRecentCount}
                  </span>
                )}
                {sidebarOpen && item.isPlaceholder && (
                  <span className="ml-auto text-[9px] uppercase tracking-wider" style={{ color: "oklch(0.28 0.04 240)" }}>Soon</span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Gold separator — mirrors the one above nav */}
        <div style={{ height: "1px", margin: "0 16px", background: "oklch(0.72 0.14 75)" }} />

        {/* User profile + logout — compact single row */}
        <div className="px-3 py-1.5">
          <div className="flex items-center gap-2">
            <div
              className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
              style={{
                background: `${roleColor}25`,
                border: `1px solid ${roleColor}40`,
                color: roleColor,
              }}
            >
              {user.initials}
            </div>
            {sidebarOpen && (
              <>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold truncate" style={{ color: "oklch(0.12 0.04 240)" }}>{user.name}</p>
                </div>
                {/* Change Password — available to all users */}
                <button
                  onClick={() => setShowChangePassword(true)}
                  className="shrink-0 w-6 h-6 flex items-center justify-center rounded transition-colors"
                  style={{ color: "oklch(0.45 0.03 240)" }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = "oklch(0.72 0.14 75)"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = "oklch(0.45 0.03 240)"; }}
                  title="Change Password"
                >
                  {Icon.key}
                </button>
                <button
                  onClick={logout}
                  className="shrink-0 w-6 h-6 flex items-center justify-center rounded transition-colors"
                  style={{ color: "oklch(0.45 0.03 240)" }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = "oklch(0.55 0.01 240)"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = "oklch(0.45 0.03 240)"; }}
                  title="Sign out"
                >
                  {Icon.logout}
                </button>
              </>
            )}

            {/* Collapsed sidebar: key icon — all users */}
            {!sidebarOpen && (
              <button
                onClick={() => setShowChangePassword(true)}
              className="shrink-0 w-7 h-7 flex items-center justify-center rounded transition-colors"
              style={{ color: "oklch(0.28 0.04 240)" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = "oklch(0.55 0.01 240)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = "oklch(0.45 0.03 240)"; }}
              title="Change password"
              >
                {Icon.key}
              </button>
            )}
          </div>
        </div>
      </aside>

      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
      )}

      {/* ── Main content ── */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Preview mode banner — shown when super_admin is previewing another role */}
        {isPreviewMode && (
          <div
            className="shrink-0 flex items-center gap-3 px-5 py-2"
            style={{
              background: "oklch(0.92 0.005 240 / 15%)",
              borderBottom: "1px solid oklch(0.92 0.005 240 / 40%)",
            }}
          >
            <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} style={{ color: "oklch(0.08 0.01 240)" }}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            <p className="text-sm text-[oklch(0.08_0.01_240)] flex-1">
              <span className="font-semibold">Preview Mode:</span> You are viewing the{" "}
              <span className="font-bold" style={{ color: "oklch(0.08 0.01 240)" }}>{ROLE_LABELS[previewRole!]}</span>{" "}
              interface. Your actual role (Director) is unchanged.
            </p>
            <button
              onClick={exitPreview}
              className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold transition-all duration-150"
              style={{
                background: "oklch(0.55 0.18 30)",
                color: "oklch(0.98 0.01 75)",
                border: "none",
                cursor: "pointer",
                boxShadow: "0 2px 8px oklch(0.55 0.18 30 / 40%)",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.background = "oklch(0.48 0.18 30)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.background = "oklch(0.55 0.18 30)";
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              Exit Preview
            </button>
          </div>
        )}

        {/* Security banner removed — password change accessible via sidebar Change Password link */}

        {/* Top header bar */}
        <header
          className="shrink-0 h-18 flex items-center px-6 gap-2 relative"
          style={{
            background: "#E4DDD3",
            borderBottom: "1px solid #D8D2C8",
          }}
        >
          <div className="flex items-center gap-2 w-full min-w-0">
            <div className="shrink-0">
              <h2
                className="text-base font-bold whitespace-nowrap"
                style={{ fontFamily: "'Playfair Display', serif", color: "oklch(0.12 0.04 240)" }}
              >
                {isPreviewMode ? ROLE_LABELS[effectiveRole] : ROLE_LABELS[user.role]}
              </h2>
            </div>
            {/* Scrollable tool buttons — overflow-x:auto only, bell is OUTSIDE this div */}
            <div className="flex-1 flex items-center gap-2 flex-nowrap overflow-x-auto min-w-0" style={{ scrollbarWidth: 'none' }}>
              {/* APX Intelligence button -- hidden for bookkeepers only */}
              {user.role !== "bookkeeper" && (
                <Link
                  href="/intelligence"
                  className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-sm font-semibold transition-all duration-150 shrink-0 whitespace-nowrap"
                  title="APX Intelligence"
                  style={{
                    background: (location === "/intelligence" || location === "/search-preview")
                      ? "oklch(0.55 0.01 240)"
                      : "oklch(0.90 0.02 85)",
                    color: (location === "/intelligence" || location === "/search-preview")
                      ? "oklch(0.98 0.005 85)"
                      : "oklch(0.35 0.01 240)",
                    border: (location === "/intelligence" || location === "/search-preview")
                      ? "1px solid oklch(0.55 0.01 240)"
                      : "1px solid oklch(0.82 0.02 85 / 60%)",
                  }}
                  onMouseEnter={(e) => {
                    if (location !== "/intelligence" && location !== "/search-preview") {
                      (e.currentTarget as HTMLElement).style.background = "oklch(0.55 0.01 240)";
                      (e.currentTarget as HTMLElement).style.color = "oklch(0.98 0.005 85)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (location !== "/intelligence" && location !== "/search-preview") {
                      (e.currentTarget as HTMLElement).style.background = "oklch(0.90 0.02 85)";
                      (e.currentTarget as HTMLElement).style.color = "oklch(0.35 0.01 240)";
                    }
                  }}
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  <span>APX Intelligence</span>
                </Link>
              )}
              {user.role !== "bookkeeper" && (
                <Link
                  href="/red-team-v2"
                  className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-sm font-semibold transition-all duration-150 shrink-0 whitespace-nowrap"
                  title="Biz Model Stress Test"
                  style={{
                    background: location === "/red-team-v2"
                      ? "oklch(0.35 0.12 25)"
                      : "oklch(0.94 0.02 25)",
                    color: location === "/red-team-v2"
                      ? "oklch(0.98 0.005 85)"
                      : "oklch(0.35 0.12 25)",
                    border: location === "/red-team-v2"
                      ? "1px solid oklch(0.35 0.12 25)"
                      : "1px solid oklch(0.80 0.04 25 / 60%)",
                  }}
                  onMouseEnter={(e) => {
                    if (location !== "/red-team-v2") {
                      (e.currentTarget as HTMLElement).style.background = "oklch(0.35 0.12 25)";
                      (e.currentTarget as HTMLElement).style.color = "oklch(0.98 0.005 85)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (location !== "/red-team-v2") {
                      (e.currentTarget as HTMLElement).style.background = "oklch(0.94 0.02 25)";
                      (e.currentTarget as HTMLElement).style.color = "oklch(0.35 0.12 25)";
                    }
                  }}
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span>Red Team</span>
                </Link>
              )}
              <div className="w-px h-4 mx-1" style={{ background: "oklch(0.85 0.02 220)" }} />
              {/* External tool quick-launch */}
              {EXTERNAL_TOOLS.map((tool) => (
                <a
                  key={tool.label}
                  href={tool.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={tool.label}
                  className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-sm font-medium transition-all duration-150 shrink-0 whitespace-nowrap"
                  style={{
                    background: "oklch(0.90 0.02 85)",
                    color: "oklch(0.18 0.02 240)",
                    border: "1px solid oklch(0.82 0.02 85 / 60%)",
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.background = "oklch(0.86 0.02 85)";
                    (e.currentTarget as HTMLElement).style.borderColor = "oklch(0.75 0.02 85)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.background = "oklch(0.90 0.02 85)";
                    (e.currentTarget as HTMLElement).style.borderColor = "oklch(0.82 0.02 85 / 60%)";
                  }}
                >
                  {tool.icon}
                  <span>{tool.label}</span>
                </a>
              ))}
              <XeroStatusDots />
            </div>
            {/* Notification bell + date + user — OUTSIDE the overflow-x-auto div so badge is never clipped */}
            <div className="flex items-center gap-2 shrink-0 pl-2">
              <div className="w-px h-4" style={{ background: "oklch(0.85 0.02 220)" }} />
              {/* Bell wrapper with explicit overflow:visible so badge shows above */}
              <div style={{ overflow: 'visible', position: 'relative', zIndex: 50 }}>
                <NotificationBell />
              </div>
              <div className="w-px h-4" style={{ background: "oklch(0.85 0.02 220)" }} />
              <span className="text-sm shrink-0 whitespace-nowrap" style={{ color: "oklch(0.28 0.04 240)" }}>
                {new Date().toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
              </span>
              <div className="w-px h-4" style={{ background: "oklch(0.85 0.02 220)" }} />
              <span className="text-sm font-semibold shrink-0 whitespace-nowrap" style={{ color: "oklch(0.12 0.04 240)" }}>
                {user.name}
              </span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className={`flex-1 overflow-y-auto${noPadding ? "" : " p-6"}`} style={{ background: "#F8F6F2" }}>
          {children}
        </main>
      </div>
    </div>
  );
}
